import { EntityLocationDTO } from '@user/store/map/typings'

// Pure helpers extracted from useMapLibreMarkers so they can be unit-tested
// without importing maplibre-gl (which executes worker setup on import and
// breaks under jsdom).

export type GeoJsonPointFeature = {
  type: 'Feature'
  geometry: { type: 'Point'; coordinates: [number, number] }
  properties: Record<string, unknown>
}

// Pin Density (SH-919): at max zoom, every pin must show regardless of the
// density setting — there's no further zoom level left to separate
// overlapping pins into.
export function computeAllowOverlap(
  collisionDetectionEnabled: boolean,
  isAtMaxZoom: boolean,
): boolean {
  return !collisionDetectionEnabled || isAtMaxZoom
}

export const SORT_KEY_TIER = 1_000_000

function fnv1a(value: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

export function computeSortKey(location: EntityLocationDTO): number {
  const isMultiRecord = !!location.multitenant || (location.count ?? 1) > 1
  return (
    (isMultiRecord ? 0 : SORT_KEY_TIER) +
    (fnv1a(location.pinId ?? '') % SORT_KEY_TIER)
  )
}

export function getIconKey(
  location: EntityLocationDTO,
  selected: boolean,
  isD2dEnabled: boolean,
): string {
  const ksColor = isD2dEnabled
    ? (location.knockStatusSettingColor ?? 'none')
    : 'none'
  const ksIcon = isD2dEnabled
    ? (location.knockStatusSettingIcon ?? 'none')
    : 'none'
  const lost = location.isLost ? 'lost' : 'open'
  return `${location.entityType}-${ksColor}-${ksIcon}-${location.multitenant ? 'mt' : 'st'}-${lost}-${selected ? 'sel' : 'def'}`
}

export function locationToFeature(
  location: EntityLocationDTO,
  iconKey: string,
  selectedIconKey: string,
  iconOffsetX: number,
  iconOffsetY: number,
): GeoJsonPointFeature {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [location.longitude, location.latitude],
    },
    properties: {
      pinId: location.pinId,
      entityId: location.entityId,
      entityType: location.entityType,
      serviceLocationId: location.serviceLocationId,
      count: location.count,
      multitenant: location.multitenant,
      assigneeId: location.assigneeId ?? null,
      isLost: location.isLost ?? false,
      latitude: location.latitude,
      longitude: location.longitude,
      knockStatusSettingColor: location.knockStatusSettingColor ?? null,
      knockStatusSettingIcon: location.knockStatusSettingIcon ?? null,
      knockStatusLocationId: location.knockStatusLocationId ?? null,
      contactLocationId: location.contactLocationId ?? null,
      pestPacLocationId: location.pestPacLocationId ?? null,
      knockStatusReason: location.knockStatusReason ?? null,
      knockStatusSettingName: location.knockStatusSettingName ?? null,
      knockStatusCreatedTime: location.knockStatusCreatedTime ?? null,
      knockStatusCreatedByName: location.knockStatusCreatedByName ?? null,
      knockStatusCount: location.knockStatusCount ?? null,
      knockStatusHistoryId: location.knockStatusHistoryId ?? null,
      address: JSON.stringify(
        location.address ?? {
          street1: location.street1 ?? null,
          city: location.city ?? null,
          stateAbbreviation: location.stateAbbreviation ?? null,
          postalCode: location.postalCode ?? null,
          country: location.country ?? null,
          latitude: location.latitude,
          longitude: location.longitude,
          knockStatusLocationId: location.knockStatusLocationId ?? null,
        },
      ),
      entities: JSON.stringify(location.entities ?? []),
      iconKey,
      selectedIconKey,
      iconOffset: [iconOffsetX, iconOffsetY],
      sortKey: computeSortKey(location),
    },
  }
}

export function featureToLocation(
  props: Record<string, unknown>,
): EntityLocationDTO {
  return {
    pinId: props.pinId as string,
    entityId: props.entityId as number,
    entityType: props.entityType as string,
    serviceLocationId: props.serviceLocationId as number,
    count: props.count as number,
    multitenant: props.multitenant as boolean,
    isLost: props.isLost as boolean,
    latitude: props.latitude as number,
    longitude: props.longitude as number,
    knockStatusSettingColor: props.knockStatusSettingColor as
      | string
      | null
      | undefined,
    knockStatusSettingIcon: props.knockStatusSettingIcon as
      | number
      | null
      | undefined,
    knockStatusLocationId: props.knockStatusLocationId as
      | number
      | null
      | undefined,
    contactLocationId: props.contactLocationId as number | null | undefined,
    pestPacLocationId: props.pestPacLocationId as string | null | undefined,
    knockStatusReason: props.knockStatusReason as string | null | undefined,
    knockStatusSettingName: props.knockStatusSettingName as
      | string
      | null
      | undefined,
    knockStatusCreatedTime: props.knockStatusCreatedTime as
      | string
      | null
      | undefined,
    knockStatusCreatedByName: props.knockStatusCreatedByName as
      | string
      | null
      | undefined,
    knockStatusCount: props.knockStatusCount as number | null | undefined,
    knockStatusHistoryId: props.knockStatusHistoryId as
      | number
      | null
      | undefined,
    address: JSON.parse((props.address as string) ?? 'null') ?? undefined,
    entities: JSON.parse((props.entities as string) ?? '[]'),
    assigneeId: (props.assigneeId as string | null) ?? undefined,
  } as EntityLocationDTO
}
